import { authFetch } from '../../utils/authFetch';
// ─────────────────────────────────────────────────────────────
// riskScenarioActions.js  (all “SCENARIO” constants)
// ─────────────────────────────────────────────────────────────
import {
    /* fetch */
    FETCH_RISK_SCENARIOS_START,
    FETCH_RISK_SCENARIOS_SUCCESS,
    FETCH_RISK_SCENARIOS_FAILURE,
    /* refine */
    REFINE_SCENARIO_START,
    REFINE_SCENARIO_SUCCESS,
    REFINE_SCENARIO_FAILURE,
    /* promote / clear */
    MOVE_SCENARIO_TO_RISKS,
    CLEAR_SCENARIOS,
  } from './actionTypes';

import { v4 as uuidv4 } from 'uuid';
  
  /* ----------------------------------------------------------- */
  /* 1. generate variants                                        */
  /* ----------------------------------------------------------- */
// src/redux/actions/riskScenarioActions.js  (extract)

export const fetchRiskScenarios =
  (modelId, assessmentUuid, card, modelNarratives) => async dispatch => {
    const { section: riskArea, title: sourceName } = card;

    dispatch({
      type   : FETCH_RISK_SCENARIOS_START,
      payload: { modelId, riskArea, sourceName }
    });

    try {
      const res = await authFetch('/.netlify/functions/generateRiskScenarios',{
        method :'POST',
        headers:{ 'Content-Type':'application/json' },
        body   : JSON.stringify({ card, modelNarratives })
      });
      if (!res.ok) throw new Error(await res.text());

      const { scenarios } = await res.json();
      dispatch({
        type   : FETCH_RISK_SCENARIOS_SUCCESS,
        payload: { modelId, riskArea, sourceName, scenarios }
      });
    } catch (err) {
      dispatch({
        type   : FETCH_RISK_SCENARIOS_FAILURE,
        payload: { modelId, error: err.message }
      });
    }
};

/* ----------------------------------------------------------- */
/* 1b. generate scenarios using Supabase RAG catalogues        */
/* ----------------------------------------------------------- */
export const fetchRiskScenariosWithRAG =
  (modelId, assessmentUuid, card, modelNarratives, selectedCatalogues = [], useAllCatalogues = true) => async dispatch => {
    const { section: riskArea, title: sourceName } = card;

    dispatch({
      type   : FETCH_RISK_SCENARIOS_START,
      payload: { modelId, riskArea, sourceName }
    });

    try {
      const res = await authFetch('/.netlify/functions/generateScenariosWithRAG', {
        method :'POST',
        headers:{ 'Content-Type':'application/json' },
        body   : JSON.stringify({ 
          card, 
          modelNarratives,
          selectedCatalogues,
          useAllCatalogues
        })
      });
      if (!res.ok) throw new Error(await res.text());

      const { scenarios, method, cataloguesUsed, retrievedRisks } = await res.json();
      
      // Enhance scenarios with catalogue source information
      const enhancedScenarios = scenarios.map(scenario => ({
        ...scenario,
        generationMethod: method,
        cataloguesUsed: cataloguesUsed || [],
        retrievedContext: retrievedRisks || []
      }));

      dispatch({
        type   : FETCH_RISK_SCENARIOS_SUCCESS,
        payload: { 
          modelId, 
          riskArea, 
          sourceName, 
          scenarios: enhancedScenarios,
          cataloguesUsed,
          generationMethod: method
        }
      });
    } catch (err) {
      dispatch({
        type   : FETCH_RISK_SCENARIOS_FAILURE,
        payload: { modelId, error: err.message }
      });
    }
};
  
  /* ----------------------------------------------------------- */
  /* 2. refine a variant                                         */
  /* ----------------------------------------------------------- */
// only the refineScenario thunk shown – rest of file unchanged
export const refineScenario = ({
  modelId,
  assessmentUuid,
  sectionName,     // risk area
  category,        // source name
  originalName,    // scenarioId
  originalRiskText,
  userInstruction  // ← string from Chips / textarea
}) => async (dispatch) => {
  dispatch({ type: REFINE_SCENARIO_START, payload: { modelId } });

  try {
    const res = await authFetch('/.netlify/functions/refineScenario', {
      method : 'POST',
      headers: { 'Content-Type':'application/json' },
      body   : JSON.stringify({
        modelId,
        assessmentUuid,
        sectionName,
        category,
        originalName,
        originalRiskText,
        userInstruction
      })
    });

    if (!res.ok) throw new Error(await res.text());
    const { refinedRisk, rationale } = await res.json();

    dispatch({
      type   : REFINE_SCENARIO_SUCCESS,
      payload: {
        modelId,
        riskArea  : sectionName,
        sourceName: category,
        scenario  : {
          scenarioId  : originalName,
          scenarioText: refinedRisk,
          rationale
        }
      }
    });
  } catch (err) {
    dispatch({
      type   : REFINE_SCENARIO_FAILURE,
      payload: { modelId, error: err.message }
    });
  }
};

  /* ----------------------------------------------------------- */
  /* 3. promote scenario → Saved risk & persist                  */
  /* ----------------------------------------------------------- */
  export const promoteScenarioToSaved =
  ({ modelId, assessmentUuid, riskArea, sourceName, scenario, owner }) =>
  async dispatch => {
    try {
      /* --------  build backend payload  -------- */
      const riskId = scenario.scenarioId || uuidv4();

      const payload = {
        riskId,
        name       : scenario.scenarioText || scenario.variantText || '',
        section    : riskArea,
        category   : sourceName,
        type       : 'Scenario',
        rag_source_file: null,
        modelUuid  : modelId,
        assessmentUuid,
        owner
      };

      /* --------  POST → /.netlify/functions/saveRisk  -------- */
      const response = await authFetch('/.netlify/functions/saveRisk', {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('[promoteScenarioToSaved] saveRisk successful:', result);

      /* --------  only update Redux after successful save  -------- */
      dispatch({
        type   : MOVE_SCENARIO_TO_RISKS,
        payload: {
          assessmentUuid,  // Fixed: reducer expects assessmentUuid, not modelId
          riskArea,
          sourceName,
          scenario: {
            ...scenario,
            riskId: result.riskId || riskId  // use confirmed riskId from backend
          },
          owner               // <-- keep for reducer
        }
      });

    } catch (err) {
      console.error('[promoteScenarioToSaved] saveRisk failed:', err);
      // Don't update Redux state on failure - let the error bubble up
      throw err;
    }
  };
  
  /* 4. clear remain unchanged */
  export const moveScenarioToRisks =
    (assessmentUuid, riskArea, sourceName, scenario) => ({
      type   : MOVE_SCENARIO_TO_RISKS,
      payload: { assessmentUuid, riskArea, sourceName, scenario }
  });
  
  export const clearScenarios =
    (modelId, riskArea, sourceName) => ({
      type   : CLEAR_SCENARIOS,
      payload: { modelId, riskArea, sourceName }
  });